import type { ReactNode } from 'react';
import { Button, Close, Modal } from '../design-system';
import { ShippingCountdownHeader } from './ShippingCountdownHeader';
import { ShippingPositions } from './ShippingPositions';
import type {
  ShippingCountdown,
  ShippingModalButton,
  ShippingPosition,
} from './types';

export interface ShippingModalProps {
  /** Backdrop / X-button close. */
  onClose: () => void;

  // ─── Optional sections (render only when provided) ──────────────
  /** "Time left to confirm shipping" + h/m chips. Reveal flow only. */
  countdown?: ShippingCountdown;
  /** Bold heading at the top of the sheet (disclaimer "Disclaimer"). */
  title?: ReactNode;
  /** Lead paragraph ABOVE the breakdown (disclaimer intro). ReactNode so
   *  callers can pass styled / multi-line content. */
  description?: ReactNode;
  /** Cost lines of the breakdown card (shipping fee, tax, …). */
  positions?: ShippingPosition[];
  /** Emphasised Total row under the positions. */
  total?: ShippingPosition;
  /** Fine print BELOW the breakdown — the reveal 48h/FMV notice or the
   *  disclaimer "With buyback…" emphasis. */
  note?: ReactNode;

  // ─── Buttons (stock Design Constructor `Button` variants) ───────
  /** Primary CTA inside the white sheet — Buyback. */
  primary: ShippingModalButton;
  /** Secondary pill BELOW the sheet on the dimmed backdrop —
   *  "Confirm Shipping" / "Add Delivery". Optional. */
  secondary?: ShippingModalButton;

  /** `aria-labelledby` target id for the dialog. */
  ariaLabelledBy?: string;
}

/**
 * The single, reusable shipping modal — Figma 703:28734.
 *
 * One component, ONE Design Constructor entry (`--co-ShippingModal-*`),
 * reused across every "ship vs buyback" decision in the app. Each visual
 * block is optional and driven by props, so the same modal renders the
 * rich reveal-flow confirm (countdown + full fee breakdown + warning) AND
 * the leaner My Packs ship disclaimer (description + a couple of fees) —
 * no duplicate components, no diverging designs.
 *
 * Layout (top → bottom), each section optional except the primary CTA:
 *   1. countdown header        — `countdown`
 *   2. fees breakdown card     — `positions` (+ `total`)
 *   3. description / fine print — `description`
 *   4. primary CTA (Buyback)   — `primary`            ← inside the sheet
 *   5. secondary pill          — `secondary`          ← below, on backdrop
 *
 * Backdrop dim/blur comes from the SHARED DS `Modal` (`--co-Modal-*`),
 * uniform with every other modal. Buttons are stock generic `Button`
 * variants tuned through the standard `--co-Button-*` knobs.
 */
export function ShippingModal({
  onClose,
  countdown,
  title,
  description,
  positions,
  total,
  note,
  primary,
  secondary,
  ariaLabelledBy,
}: ShippingModalProps) {
  return (
    <Modal
      onClose={onClose}
      inspectName="ShippingModal"
      {...(ariaLabelledBy ? { ariaLabelledBy } : {})}
    >
      <div className="relative flex flex-col items-stretch gap-4">
        {/* Close (X) — fixed top-right on mobile, half-overlapping the
            card on ≥640 px. translate utilities only (no inline
            transform) to dodge the Tailwind 4 transform-merge quirk. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="
            fixed top-4 right-4 z-[60]
            sm:absolute sm:top-0 sm:right-0 sm:z-10
            sm:translate-x-full sm:-translate-y-1/2
            inline-flex h-10 w-10 items-center justify-center rounded-full
            transition-opacity hover:opacity-90 active:scale-95
          "
          style={{
            backgroundColor: 'var(--co-ShippingModal-closeFill, rgba(0,0,0,0.6))',
            color: 'var(--co-ShippingModal-closeIconColor, #FFFFFF)',
          }}
        >
          <Close size={18} strokeWidth={2} />
        </button>

        {/* White sheet — everything except the secondary pill. Tagged
            so the DC inspector opens the single ShippingModal panel from
            any empty area of the card. */}
        <section
          {...(ariaLabelledBy ? { id: ariaLabelledBy } : {})}
          data-co-component="ShippingModal"
          className="flex flex-col gap-5 rounded-[24px] p-5"
          style={{ backgroundColor: 'var(--co-ShippingModal-cardFill, #FFFFFF)' }}
        >
          {countdown && <ShippingCountdownHeader countdown={countdown} />}

          {(title || description) && (
            <div className="flex flex-col gap-2">
              {title && (
                <h2
                  className="font-bold"
                  style={{
                    color: 'var(--co-ShippingModal-titleColor, #15181E)',
                    fontSize: 'var(--co-ShippingModal-titleSize, 17px)',
                    letterSpacing: '-0.3px',
                    lineHeight: 1.25,
                  }}
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  style={{
                    color: 'var(--co-ShippingModal-descColor, rgba(0,0,0,0.75))',
                    fontFamily:
                      'var(--co-ShippingModal-descFont, "Inter"), Inter, sans-serif',
                    fontSize: 'var(--co-ShippingModal-descSize, 16px)',
                    lineHeight: 1.4,
                  }}
                >
                  {description}
                </p>
              )}
            </div>
          )}

          {positions && positions.length > 0 && (
            <ShippingPositions positions={positions} {...(total ? { total } : {})} />
          )}

          {note && (
            <p
              style={{
                color: 'var(--co-ShippingModal-noteColor, rgba(0,0,0,0.75))',
                fontFamily:
                  'var(--co-ShippingModal-noteFont, "Inter"), Inter, sans-serif',
                fontSize: 'var(--co-ShippingModal-noteSize, 16px)',
                // Unitless so line-height scales with the size knob.
                lineHeight: 1.4,
              }}
            >
              {note}
            </p>
          )}

          {/* Primary CTA — stock `primary` variant, tall full-width pill
              with the gloss shadow (mirrors the WinnerStep Buyback). */}
          <Button
            variant="primary"
            size="lg"
            onClick={primary.onClick}
            loading={primary.loading ?? false}
            disabled={primary.disabled ?? false}
            fullWidth
            className="h-16"
          >
            {primary.label}
          </Button>
        </section>

        {/* Secondary pill — sits BELOW the sheet on the dimmed backdrop.
            `glass-frosted` reads as a dark frosted pill over the dark
            page (same variant the My Packs "Add Delivery" pill uses), so
            it matches the Figma dark "Confirm Shipping" button. Hugs its
            label; `self-center` opts out of the column's stretch. */}
        {secondary && (
          <Button
            variant="glass-frosted"
            size="lg"
            onClick={secondary.onClick}
            loading={secondary.loading ?? false}
            disabled={secondary.disabled ?? false}
            className="self-center"
          >
            {secondary.label}
          </Button>
        )}
      </div>
    </Modal>
  );
}
